import * as DefaultLocaleModule from "../locale/index.js";
export { Locale };
/**
 * Локаль.
 * 
 * @example
 * import { Locale } from "../personal-studio-components/frame/index.js"
 * const local = new Locale("ComponentName");
 * console.log(local.message("msg_id", { param1: 1, param2: 2 }));
 * @author Алексей Васьковский <avaskovskiy@umney.ru> 
 */
class Locale {
  /**
   * Выводить сообщения для отладки модуля.
   * @type {boolean}
   * @default true
   */
  debugging = true;
  /**
   * @param {string} [component=Message] - Идентификатор компонента.
   * @param {string} [lang] - Язык локализации.
   * Если не указан, определяется из настроек браузера.
   * @param {Object} [module=personal-studio-components/locale] -
   * Модуль с локализованными сообщениями.
   */
  constructor(component = "Message", defaultMessage = {}) {
    this.component = component;
    this.defaultMessage = defaultMessage;
    this.message = this.message.bind(this);
  }
  // Загружает шаблон локализованного сообщения
  getTemplate(message) {
    for (const module of modules) {
      if (module[lang]
        && module[lang][this.component]
        && module[lang][this.component][message])
        return module[lang][this.component][message];
    }
    if (this.defaultMessage[message])
      return this.defaultMessage[message];
    for (const module of modules) {
      if (module[lang]
        && module[lang]["Message"]
        && module[lang]["Message"][message])
        return module[lang]["Message"][message];
    }
    return message;
  }
  /**
   * @param {string} message - Идентификатор сообщения.
   * @param {Object} [data={}] - Данные для подстановки.
   * @returns {string} Локализованное сообщение.
   * @example
   * console.log(local.message("msg_id", { param1: 1, param2: 2 }));
   */
  message(message, data = {}) {
    const template = this.getTemplate(message);
    return template.replace(/{(\w+)}/g, (match, param_id) =>
      undefined === data[param_id] ? "" : this.value(data[param_id]));
  }
  // Локализованное значение произвольного типа
  value(value) {
    if (null === value || undefined === value)
      return "";
    if (value instanceof Date)
      return this.timestamp(value);
    if ("number" === typeof value)
      return this.number(value);
    if ("boolean" === typeof value)
      return this.boolean(value);
    else
      return String(value);
  }
  /**
   * @param {boolean} value 
   * @returns {string} Булево значение в локальном формате.
   */
  boolean(value) {
    return value ? local.message("true") : local.message("false");
  }
  /**
   * @param {number} value 
   * @returns {string} Число в локальном формате. 
   */
  number(value) {
    return new Number(value).toLocaleString(lang);
  }
  /**
   * @param {Date} [value] - Дата и время. 
   * @returns {string} Дата и время в локальном формате.
   */
  timestamp(value) {
    value ??= new Date;
    return value.toLocaleString(lang);
  }
  /**
   * @param {Date} [value] - Дата 
   * @returns {string} Дата в локальном формате.
   */
  date(value) {
    value ??= new Date;
    return value.toLocaleDateString(lang);
  }
  /**
   * @param {Date} [value] - Время
   * @returns {string} Время в локальном формате.
   */
  time(value) {
    value ??= new Date;
    return value.toLocaleTimeString(lang);
  }
  // Обёртка для сообщений
  wrapper(wrapper, message, data) {
    const localized = this.message(message, data);
    const wrapped = local.message(wrapper,
      { message: localized, component: this.component });
    return wrapped;
  }
  /**
   * Выводит сообщение в консоль без локализации, но с префиксом компонента,
   * если включён debugging. 
   * @param {string} message - Идентификатор сообщения.
   * @param {Object} data - Данные для подстановки в сообщение и отладки.
   * @returns {void}
   */
  debug(message, data) {
    if (this.debugging)
      console.debug("%s: %s", this.component, message,
        undefined === data ? " " : data);
  }
  /**
   * Выводит сообщение в консоль без локализации, но с префиксом компонента.
   * @param {string} message - Идентификатор сообщения.
   * @param {Object} data - Данные для подстановки в сообщение и отладки.
   * @returns {void}
   */
  log(message, data) {
    console.log("%s: %s", this.component, message,
      undefined === data ? " " : data);
  }
  /**
   * Ошибка пользователя или среды выполнения.
   * Сообщение дублируется в консоль без локализации.
   * @param {string} message - Идентификатор сообщения.
   * @param {Object} data - Данные для подстановки в сообщение и отладки.
   * @returns {Error} Исключение для `throw` с локализованным сообщением.
   */
  error(message, data) {
    console.error("%s: %s", this.component, message,
      undefined === data ? " " : data);
    message = this.wrapper("error", message, data);
    return new Error(message);
  }
  /**
   * Ошибка при использовании компонента.
   * Некорректное значение аргумента или состояния.
   * Сообщение дублируется в консоль без локализации.
   * @param {string} name - Имя аргумента.
   * @param {any} value - Значение аргумента.
   * @returns {Error} Исключение для `throw` с локализованным сообщением.
   */
  invalid(name, value) {
    console.error("%s: invalid %s: %s", this.component, name, typeof value,
      undefined === value ? " " : value);
    const message = local.message("invalid", {
      component: this.component,
      name,
      type: typeof value,
      value: undefined === value? "undefined": JSON.stringify(value)
    });
    return new Error(message);
  }
  /**
   * Утверждение (см. [console.assert][assert]).
   * Проверка на внутренние ошибки компонента.
   *
   * Если assertion == false, выбрасывает локализованное исключение.
   *
   * Сообщение дублируется в консоль без локализации.
   *
   * [assert]: https://nodejs.org/api/console.html#consoleassertvalue-message
   * @param {boolean} assertion - Утверждение.
   * @param {Object} [data] - Данные для отладки.
   * @return {void}
   */
  assert(assertion, data) {
    if (!assertion) {
      console.error("%s: assertion failed", this.component,
        undefined === data ? " " : data);
      const message = local.message("assert",
        {
          component: this.component,
          data: undefined === data ? "" : JSON.stringify(data)
        });
      throw new Error(message);
    }
  }
  /**
   * Добавляет модуль локализации.
   * @param {Object} module - Модуль локализации,
   * аналогичный `personal-studio-components/locale`.
   * @returns {void}
   */
  static addModule(module) {
    modules.unshift(module);
  }
  /**
   * @returns {string} Текущий язык пользователя.
   */
  static getUserLanguage() {
    return lang;
  }
  /**
   * Сохраняет язык пользователя в localStorage и обновляет страницу.
   * @param {string} lang - двухбуквенный код языка ISO.
   * @returns {void}
   */
  static setUserLanguage(lang) {
    if ("string" !== typeof lang)
      throw local.invalid("lang", lang);
    localStorage.setItem(localStorageID, lang);
    location.reload();
  }
}
const local = new Locale("Locale");
const localStorageID = "personal-studio-components/lang";
const lang = getDefaultUserLanguage();
const modules = [DefaultLocaleModule];

// Определяем язык пользователя по умолчанию
function getDefaultUserLanguage() {
  const saved = localStorage.getItem(localStorageID);
  if (saved) return saved;
  for (const locale of navigator.languages) {
    const lang = locale.substring(0, 2);
    if (DefaultLocaleModule[lang]) return lang;
  }
  return "ru";
}
